/**
 * Voice Interface Component
 * Main interface for voice interaction with Anouk
 * Supports demo mode without Retell.ai
 */

import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import type { WebCallSession } from 'retell-client-js-sdk';
import { useCallStore } from '@/stores/callStore';
import { retellService } from '@/services/retell.service';
import { AudioVisualizer } from './AudioVisualizer';
import { MicrophoneButton } from './MicrophoneButton';
import { fadeInUp, staggerChildren } from '@/utils/constants';

// Check if demo mode is enabled
const isDemoMode = import.meta.env.VITE_DEMO_MODE === 'true';

/**
 * Resolves with the call id as soon as the SDK assigns it.
 *
 * The SDK sets `session.callId` right after `POST /v3/create-web-call` returns —
 * before the WebRTC transport finishes connecting — but exposes no event for it.
 * Polling lets the Worker WebSocket attach as early as it did under v2 (in parallel
 * with transport setup) instead of waiting for `session.ready`, which only settles
 * once the call is already live.
 *
 * @param session   The pending web call session
 * @param timeoutMs How long to wait before giving up
 * @returns The Retell call id
 */
function waitForCallId(session: WebCallSession, timeoutMs = 15000): Promise<string> {
  return new Promise((resolve, reject) => {
    const startedAt = Date.now();
    const tick = () => {
      if (session.callId) return resolve(session.callId);
      if (session.status === 'ended') {
        return reject(new Error('Call ended before it was created'));
      }
      if (Date.now() - startedAt > timeoutMs) {
        return reject(new Error('Timed out waiting for Retell call id'));
      }
      setTimeout(tick, 20);
    };
    tick();
  });
}

const AGENT_GAIN = 3;

/**
 * Boosts the agent's playback volume.
 *
 * SDK v3 exposes no gain hook: it plays the remote track through a detached
 * `new Audio()` kept privately by the gateway transport, so the element never
 * enters the DOM and cannot be found from the page the way it could under v2.
 * We reach that element, take its MediaStream, render it ourselves through a
 * compressor + gain chain, and mute the original so the unboosted path goes quiet.
 *
 * Reading the stream rather than the element is deliberate: Chrome's
 * createMediaElementSource is unreliable on elements fed by `srcObject`, while
 * createMediaStreamSource handles WebRTC streams properly.
 *
 * The compressor matters on Android, where WebRTC's AGC normalises the output and
 * a bare gain increase is barely audible; compressing first makes the boost land.
 *
 * This reads a private SDK field, so it fails soft: if the internals move in a
 * later SDK release, playback simply stays at its default volume.
 *
 * @param session Live web call session
 * @param ctx     AudioContext already resumed by a user gesture
 * @param gain    Output gain multiplier
 * @returns true when the boosted graph was wired up
 */
async function boostAgentAudio(
  session: WebCallSession,
  ctx: AudioContext,
  gain = AGENT_GAIN
): Promise<boolean> {
  // attachRemote() runs on the peer connection's ontrack, which can land just after
  // `ready` resolves — poll briefly rather than giving up on the first miss.
  let el: HTMLAudioElement | undefined;
  for (let i = 0; i < 25 && !el; i++) {
    el = (session as unknown as { transport?: { audioEl?: HTMLAudioElement } }).transport?.audioEl;
    if (!el) await new Promise((resolve) => setTimeout(resolve, 40));
  }

  const stream = el?.srcObject;
  if (!el || !(stream instanceof MediaStream)) {
    console.warn('[VoiceInterface] SDK audio element unreachable — volume left at default');
    return false;
  }

  try {
    await ctx.resume();

    const source = ctx.createMediaStreamSource(stream);

    const compressor = ctx.createDynamicsCompressor();
    compressor.threshold.value = -24;
    compressor.knee.value = 10;
    compressor.ratio.value = 4;
    compressor.attack.value = 0.003;
    compressor.release.value = 0.25;

    const gainNode = ctx.createGain();
    gainNode.gain.value = gain;

    source.connect(compressor);
    compressor.connect(gainNode);
    gainNode.connect(ctx.destination);

    // Silence the SDK's own unboosted playback now that we render the stream
    el.muted = true;

    if (import.meta.env.DEV) console.log('[VoiceInterface] Agent audio boosted, gain=', gain);
    return true;
  } catch (e) {
    console.warn('[VoiceInterface] Failed to boost agent audio:', e);
    return false;
  }
}

interface VoiceInterfaceProps {
  hasCarousel?: boolean;
}

export function VoiceInterface({ hasCarousel = false }: VoiceInterfaceProps) {
  const {
    callStatus,
    isCallActive,
    audioStream,
    setCallStatus,
    setIsCallActive,
    setAudioStream,
    startCall,
    endCall,
    setError,
  } = useCallStore();

  const [isProcessing, setIsProcessing] = useState(false);
  const sessionRef = useRef<WebCallSession | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);

  /**
   * Creates an AudioContext and immediately plays a 1-sample silent buffer.
   * This must be called during a user gesture (tap/click) to:
   *  1. Satisfy iOS autoplay policy
   *  2. Force iOS audio session into "MediaPlayback" mode (loudspeaker)
   *     BEFORE the Retell SDK opens its own WebRTC session in "VoiceChat" mode (earpiece).
   *
   * This context is also what boostAgentAudio() renders the agent's stream into,
   * so it must be created during the gesture for both reasons.
   */
  const initAudioContext = (): AudioContext | null => {
    try {
      const AudioCtx = window.AudioContext || (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!AudioCtx) return null;

      audioCtxRef.current?.close();
      const ctx = new AudioCtx();
      audioCtxRef.current = ctx;

      // Play a 1-sample silent buffer — claims the loudspeaker audio session on iOS
      const buffer = ctx.createBuffer(1, 1, 22050);
      const silentSource = ctx.createBufferSource();
      silentSource.buffer = buffer;
      silentSource.connect(ctx.destination);
      silentSource.start();
      ctx.resume();

      if (import.meta.env.DEV) console.log('[VoiceInterface] AudioContext initialised (loudspeaker claimed)');
      return ctx;
    } catch (e) {
      console.warn('[VoiceInterface] initAudioContext failed:', e);
      return null;
    }
  };

  /**
   * Releases the AudioContext and the microphone stream used by the visualizer.
   */
  const releaseAudio = () => {
    audioCtxRef.current?.close();
    audioCtxRef.current = null;
  };

  // When call is ended (e.g. from "Nouvelle discussion"), disconnect the session.
  // `session.end()` is idempotent — the SDK guards on its own `ended` flag — so this
  // is safe even when the end was initiated by Retell via the onEnd hook.
  useEffect(() => {
    if (callStatus !== 'ended') return;
    if (!isDemoMode && sessionRef.current) {
      sessionRef.current.end().catch(() => {});
      sessionRef.current = null;
    }
    if (audioStream) {
      audioStream.getTracks().forEach((t) => t.stop());
      setAudioStream(null);
    }
  }, [callStatus, audioStream, setAudioStream]);

  /**
   * Handle microphone button click
   */
  const handleMicClick = async () => {
    if (isCallActive) {
      // End the call
      await handleEndCall();
    } else {
      // Start the call
      await handleStartCall();
    }
  };

  /**
   * Start voice call (demo or real)
   */
  const handleStartCall = async () => {
    setIsProcessing(true);
    setError(null);

    try {
      if (isDemoMode) {
        // Demo mode: simulate call without Retell
        await handleDemoCall();
      } else {
        // Production mode: use Retell.ai
        await handleRealCall();
      }
    } catch (error) {
      console.error('[VoiceInterface] Failed to start call:', error);
      setError(error instanceof Error ? error.message : 'Failed to start call');
      setCallStatus('error');
      sessionRef.current?.end().catch(() => {});
      sessionRef.current = null;
      releaseAudio();
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * Start demo call (no Retell.ai)
   */
  const handleDemoCall = async () => {
    console.log('[VoiceInterface] 🎮 Starting DEMO call');

    // Generate mock call ID
    const callId = `demo-call-${Date.now()}`;

    // Update store
    startCall(callId);

    // Simulate connecting
    await new Promise((resolve) => setTimeout(resolve, 1000));

    // Set call as active
    setCallStatus('active');
    setIsCallActive(true);

    console.log('[VoiceInterface] 🎮 DEMO call started');
  };

  /**
   * Start real call with Retell.ai (SDK v3)
   */
  const handleRealCall = async () => {
    const agentId = import.meta.env.VITE_AGENT_ID || 'test-agent-id';

    // Init AudioContext BEFORE Retell — forces iOS audio session to loudspeaker mode
    initAudioContext();

    // Holds the reason Retell rejected the call (bad agent, no credit, key not
    // allowed for this domain...). The hook fires before the session tears down, so
    // it is the only place that cause is available — `waitForCallId` and
    // `session.ready` only see the resulting "session ended" failure.
    const sessionError: { current: Error | null } = { current: null };

    // The SDK creates the call (/v3/create-web-call) and connects the transport.
    // It returns synchronously; `ready` settles once the call is live.
    const session = retellService.createWebCall(agentId, {
      onError: (error) => {
        sessionError.current = error;
        console.error('[Retell] Session error:', error);
      },
      onEnd: ({ disconnection_reason }) => {
        console.log('[Retell] Call ended:', disconnection_reason ?? 'unknown reason');
        // Reflect a Retell-initiated hangup in the store. No-op when we ended it
        // ourselves, since endCall() has already run.
        if (useCallStore.getState().callStatus !== 'ended') endCall();
      },
    });
    sessionRef.current = session;

    try {
      // Publish the call id as soon as it exists so the Worker WebSocket can attach
      // while the transport is still connecting.
      startCall(await waitForCallId(session));

      // Wait for the call to go live (this is also where mic permission is resolved)
      await session.ready;
    } catch (error) {
      // Surface what Retell actually said rather than our generic teardown error
      throw sessionError.current ?? error;
    }

    // v3 replaces the v2 <audio>-element dance for autoplay policy
    await session.startAudioPlayback().catch((e) => {
      console.warn('[VoiceInterface] startAudioPlayback failed:', e);
    });

    // Re-apply the volume boost lost with the v2 routing chain
    if (audioCtxRef.current) {
      await boostAgentAudio(session, audioCtxRef.current);
    }

    // Set call as active
    console.log('[VoiceInterface] Call started');
    setCallStatus('active');
    setIsCallActive(true);

    // Separate capture stream, used only to drive the AudioVisualizer.
    // AGC/noise suppression are disabled so the bars track real input level.
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        autoGainControl: false,
        noiseSuppression: false,
        echoCancellation: true,
      },
    });
    setAudioStream(stream);
  };

  /**
   * End voice call (demo or real)
   */
  const handleEndCall = async () => {
    setIsProcessing(true);

    try {
      // Disconnect the Retell transport. Retell sees this as a user hangup, so no
      // authenticated end-call request is needed (and none is possible with a
      // public key).
      if (!isDemoMode && sessionRef.current) {
        await sessionRef.current.end().catch((error) => {
          console.warn('[VoiceInterface] Failed to end Retell session:', error);
        });
        sessionRef.current = null;
      }

      // Stop the visualizer capture stream
      if (audioStream) {
        audioStream.getTracks().forEach((track) => track.stop());
        setAudioStream(null);
      }

      releaseAudio();

      // Update store
      endCall();
    } catch (error) {
      console.error('[VoiceInterface] Failed to end call:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <motion.div
      variants={staggerChildren}
      initial="initial"
      animate="animate"
      className={`flex flex-col items-center ${hasCarousel ? 'space-y-2 md:space-y-8' : 'space-y-8'}`}
    >
      {/* Audio Visualizer */}
      <motion.div variants={fadeInUp}>
        <AudioVisualizer audioStream={audioStream} isActive={isCallActive} />
      </motion.div>

      {/* Microphone Button */}
      <motion.div variants={fadeInUp}>
        <MicrophoneButton
          isActive={isCallActive}
          isProcessing={isProcessing}
          onClick={handleMicClick}
        />
      </motion.div>

      {/* Helper Text */}
      <motion.div variants={fadeInUp} className="text-center space-y-2">
        <p className="text-white/80 text-sm max-w-md">
          {isCallActive
            ? 'Parlez avec Anouk pour découvrir votre prochaine aventure'
            : 'Cliquez sur le micro pour commencer'}
        </p>

        {/* Demo Mode Badge */}
        {isDemoMode && (
          <div className="inline-flex items-center space-x-2 px-3 py-1 bg-yellow-500/20 border border-yellow-500/40 rounded-full backdrop-blur-sm">
            <span className="text-yellow-300 text-xs">🎮</span>
            <span className="text-yellow-200 text-xs font-medium">Mode Démo</span>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}
